import type jsPDF from "jspdf";
import type { UserOptions } from "jspdf-autotable";
import type {
  PdfCellAlignment,
  PdfCellVerticalAlignment,
  PdfLogoMeta,
  PdfTableColumn,
  RgbColor,
} from "./pdfExport.types";

// ─── Cell value formatting ────────────────────────────────────────────────────

/**
 * Converts any cell value to a printable string, handling edge cases such as
 * null / undefined, Date objects, and nested objects without throwing.
 */
export function safeFormatCellValue(value: unknown): string {
  if (value == null) return "";

  if (value instanceof Date) {
    return value.toLocaleDateString("es-CO");
  }

  if (typeof value === "object") {
    try {
      return JSON.stringify(value);
    } catch {
      return String(value);
    }
  }

  return String(value);
}

// ─── Column width / alignment resolution ─────────────────────────────────────

/**
 * Returns the effective per-column width weights, preferring the explicit
 * `columnWidths` override when it matches the number of columns, otherwise
 * falling back to the `widthWeight` values declared per column.
 */
export function resolveColumnWidths<T>(
  columns: PdfTableColumn<T>[],
  columnWidths: number[] | undefined
): number[] {
  if (Array.isArray(columnWidths) && columnWidths.length === columns.length) {
    return columnWidths;
  }

  return columns.map((col) =>
    typeof col.widthWeight === "number" && !Number.isNaN(col.widthWeight)
      ? col.widthWeight
      : 0
  );
}

/**
 * Returns the effective per-column alignment strings, preferring the explicit
 * `columnAlignments` override when it matches the number of columns, otherwise
 * falling back to the `align` values declared per column.
 */
export function resolveColumnAlignments<T>(
  columns: PdfTableColumn<T>[],
  columnAlignments: string[] | undefined
): string[] {
  if (
    Array.isArray(columnAlignments) &&
    columnAlignments.length === columns.length
  ) {
    return columnAlignments;
  }

  return columns.map((col) => col.align ?? "");
}

// ─── Column styles builder ────────────────────────────────────────────────────

const HALIGN_MAP: Record<string, "left" | "center" | "right"> = {
  LEFT: "left",
  CENTER: "center",
  RIGHT: "right",
};

const VALIGN_MAP: Record<PdfCellVerticalAlignment, "top" | "middle" | "bottom"> =
  {
    TOP: "top",
    MIDDLE: "middle",
    BOTTOM: "bottom",
  };

/**
 * Builds the `columnStyles` map consumed by jspdf-autotable.
 * Column widths are normalized proportionally to fill `availableWidth`.
 */
export function buildColumnStyles<T>(
  columns: PdfTableColumn<T>[],
  effectiveWidths: number[],
  effectiveAlignments: string[],
  availableWidth: number
): UserOptions["columnStyles"] {
  const colCount = columns.length;

  const hasWidths =
    effectiveWidths.length === colCount &&
    effectiveWidths.some(
      (w) => typeof w === "number" && !Number.isNaN(w) && w > 0
    );

  const hasAligns =
    effectiveAlignments.length === colCount &&
    effectiveAlignments.some((a) => typeof a === "string" && a.length > 0);

  const hasValigns = columns.some((c) => c.valign != null);

  if (!hasWidths && !hasAligns && !hasValigns) return undefined;

  const totalWeight = hasWidths
    ? effectiveWidths.reduce((sum, w) => {
        const n = typeof w === "number" ? w : 0;
        return sum + (Number.isNaN(n) ? 0 : n);
      }, 0)
    : 0;

  const styles: Record<number, Record<string, unknown>> = {};

  for (let i = 0; i < colCount; i += 1) {
    const style: Record<string, unknown> = {};

    if (hasWidths && totalWeight > 0) {
      const weight = effectiveWidths[i] ?? 0;
      const n =
        typeof weight === "number" && !Number.isNaN(weight) ? weight : 0;
      if (n > 0) {
        style.cellWidth = (n / totalWeight) * availableWidth;
      }
    }

    if (hasAligns && effectiveAlignments[i]) {
      const halign =
        HALIGN_MAP[
          effectiveAlignments[i].toUpperCase() as PdfCellAlignment
        ];
      if (halign) style.halign = halign;
    }

    const colValign = columns[i]?.valign;
    if (colValign) {
      style.valign = VALIGN_MAP[colValign];
    }

    if (Object.keys(style).length > 0) {
      styles[i] = style;
    }
  }

  return styles;
}

// ─── Logo loading ─────────────────────────────────────────────────────────────

/**
 * Loads a logo image from `url` and resolves with a `PdfLogoMeta` describing
 * its position on the page, centering the logo+title block horizontally.
 *
 * @param doc - A jsPDF instance already configured with the correct page size,
 *              font and font-size so that `getTextWidth` returns accurate values.
 * @param url - Publicly accessible URL for the logo image.
 * @param title - Optional title text to measure so the block can be centered.
 * @param desiredWidth - Logo width in PDF units.
 * @param desiredHeight - Logo height in PDF units; derived from aspect ratio when omitted.
 *
 * Rejects when the image fails to load so callers can fall back gracefully.
 */
/**
 * Converts a loaded HTMLImageElement to a base64 PNG data URL via an
 * offscreen canvas. This captures the pixel data in memory so no further
 * network requests are needed when drawing the image on each page.
 *
 * @param img - Source image element.
 * @param maxWidthPx - Optional pixel cap. When the natural image is wider,
 *   the canvas is scaled down proportionally, reducing the embedded PNG size
 *   while preserving enough resolution for the display dimensions.
 */
function imageToDataUrl(img: HTMLImageElement, maxWidthPx?: number): string {
  const naturalW = img.naturalWidth || img.width;
  const naturalH = img.naturalHeight || img.height;

  let targetW = naturalW;
  let targetH = naturalH;

  if (maxWidthPx && naturalW > maxWidthPx) {
    const scale = maxWidthPx / naturalW;
    targetW = Math.round(naturalW * scale);
    targetH = Math.round(naturalH * scale);
  }

  const canvas = document.createElement("canvas");
  canvas.width = targetW;
  canvas.height = targetH;
  const ctx = canvas.getContext("2d");
  if (!ctx) return img.src;
  ctx.drawImage(img, 0, 0, targetW, targetH);
  return canvas.toDataURL("image/png");
}

const LOGO_TOP_OFFSET = 8;  // mm from page top to logo top edge (≥6mm printer safe zone)
const LOGO_TABLE_GAP = 3;  // mm between logo bottom and table top

export function loadLogoMeta(
  doc: jsPDF,
  url: string,
  title: string | undefined,
  desiredWidth: number,
  desiredHeight: number | undefined
): Promise<PdfLogoMeta> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";

    img.onload = () => {
      const pageWidth = doc.internal.pageSize.getWidth();
      const aspectRatio =
        img.width && img.height ? img.width / img.height : 1;
      const computedHeight =
        desiredHeight ?? (aspectRatio !== 0 ? desiredWidth / aspectRatio : 20);

      const gap = title ? 4 : 0;
      const titleWidth = title ? doc.getTextWidth(title) : 0;
      const combinedWidth = desiredWidth + gap + titleWidth;
      const blockStartX = Math.max(0, (pageWidth - combinedWidth) / 2);

      // Vertically center the title baseline within the logo height.
      // jsPDF text y is the baseline; subtract ~30% of logo height to place
      // the text cap-height visually centered.
      const titleY = LOGO_TOP_OFFSET + computedHeight * 0.65;

      // Cap embedded image to 300 DPI relative to its display size in the PDF.
      // This prevents a large source image from inflating the file size while
      // still producing sharp output on screen and in print.
      const maxWidthPx = Math.ceil((desiredWidth / 25.4) * 300);
      resolve({
        dataUrl: imageToDataUrl(img, maxWidthPx),
        alias: "report-logo",
        x: blockStartX,
        y: LOGO_TOP_OFFSET,
        width: desiredWidth,
        height: computedHeight,
        titleX: title ? blockStartX + desiredWidth + gap : undefined,
        titleY,
      });
    };

    img.onerror = (event) => reject(event);
    img.src = url;
  });
}

export { LOGO_TOP_OFFSET, LOGO_TABLE_GAP };

// ─── Page header drawing ──────────────────────────────────────────────────────

/**
 * Draws the logo and/or title on the current PDF page.
 * Idempotent: safe to call inside `didDrawPage` on every page.
 */
export function drawPageHeader(
  doc: jsPDF,
  logoMeta: PdfLogoMeta | undefined,
  title: string | undefined,
  titleFontSize: number,
): void {
  if (logoMeta) {
    // Pass `alias` so jsPDF embeds the image data only once in the PDF
    // regardless of how many pages call this function.
    doc.addImage(
      logoMeta.dataUrl,
      "PNG",
      logoMeta.x,
      logoMeta.y,
      logoMeta.width,
      logoMeta.height,
      logoMeta.alias,
    );
  }

  if (title) {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(titleFontSize);

    const pageW = doc.internal.pageSize.getWidth();
    const textWidth = doc.getTextWidth(title);
    const x =
      typeof logoMeta?.titleX === "number"
        ? logoMeta.titleX
        : (pageW - textWidth) / 2;
    // Use the pre-computed titleY when a logo is present; fall back to a
    // sensible fixed position for title-only headers.
    const y = logoMeta ? logoMeta.titleY : 18;

    doc.text(title, x, y);
  }
}

// ─── Input validation ─────────────────────────────────────────────────────────

export type ValidationResult =
  | { ok: true }
  | { ok: false; error: Error };

/** Returns a validation error when the export cannot proceed, or `{ ok: true }`. */
export function validateExportInput<T>(
  columns: PdfTableColumn<T>[],
  rows: T[]
): ValidationResult {
  if (typeof window === "undefined") {
    return {
      ok: false,
      error: new Error(
        "exportTableToPdf can only run in a browser environment."
      ),
    };
  }

  if (!Array.isArray(columns) || columns.length === 0) {
    return {
      ok: false,
      error: new Error("exportTableToPdf requires at least one column."),
    };
  }

  if (!Array.isArray(rows)) {
    return {
      ok: false,
      error: new Error("exportTableToPdf requires rows to be an array."),
    };
  }

  return { ok: true };
}

// ─── Constants ────────────────────────────────────────────────────────────────

export const DEFAULT_HEADER_FILL_COLOR: RgbColor = [66, 66, 66];
